// Insertion preview for gap-opening drag.
//
// The press timeline is SEQUENCE-PACKED: bars butt up against each other and the
// server re-cascades every start time from the head of the queue. So a drop is
// not "place at 14:20" — it's "insert at position N". The old drop ghost drew
// itself at the raw cursor time, which is a position the job would never
// actually occupy, and for a Ready-to-Run card the backend appended it at the
// tail regardless. That mismatch is why planners stopped trusting the drag.
//
// This module answers the only question that matters during a drag: if I let go
// here, WHERE does the job land and what moves? Everything is derived with the
// same downtime walk the real bars use (splitRunAroundDowntime), so the preview
// and the committed result agree.
import { DowntimeWindow } from '../productionTypes';
import { splitRunAroundDowntime } from '../productionUtils';
import { GanttEntry } from './ganttTypes';

export interface InsertPreview {
  /**
   * The `sequence` value to insert AT — i.e. the sequence currently held by the
   * job this insert lands in front of (or max+1 when appending). Deliberately
   * NOT a positional index: completed rows carry stale sequence numbers that
   * don't match their on-screen order, so a positional index would be
   * mistranslated by the server. Sending the successor's own sequence is
   * unambiguous whether or not the lane is densely numbered.
   */
  seq: number;
  /** Real start / effective end, in minutes from board base. */
  startAbs: number;
  endAbs: number;
  /** Running blocks — >1 when the inserted run pauses across a closure. */
  segments: { startMin: number; endMin: number }[];
  /** Wall-clock footprint (endAbs - startAbs), i.e. how far downstream shifts. */
  shiftMin: number;
  /** Entry ids that get pushed later by this insertion. */
  pushedIds: number[];
  /** The job this insert lands in front of / behind (null at the ends). */
  beforeJob: string | null;
  afterJob: string | null;
  /**
   * Index within the planned queue. Fed back in as `prevIdx` on the next
   * pointermove so the slot doesn't flicker when the cursor sits on a midpoint.
   */
  idx: number;
}

const isPlanned = (e: GanttEntry) => e.status === 'planned';

export interface InsertArgs {
  /** All entries on the target lane (any order). */
  laneEntries: GanttEntry[];
  /** Entry being moved, when dragging an already-placed bar — excluded from the queue. */
  movingId?: number | null;
  /** Planned duration of the job(s) being inserted, in minutes. */
  durationMin: number;
  /** Cursor position on the timeline, minutes from board base. */
  cursorAbs: number;
  /** Raw downtime windows, so the preview pauses across closures like a real bar. */
  windows: DowntimeWindow[];
  /** Board base (today 00:00) as epoch ms — for the ISO round-trip. */
  baseMs: number;
  /** Now, minutes from base — the floor for an insert onto an empty lane. */
  nowAbs: number;
  /** Previous preview's `idx`, for slot hysteresis. Omit on the first move. */
  prevIdx?: number | null;
  /**
   * How far past a bar's midpoint the cursor must travel before the slot flips,
   * in minutes. Without this the slot chatters between two positions whenever
   * the cursor rests near a midpoint, which reads as the board being unsure.
   * Callers convert a pixel dead-zone at the current zoom into minutes.
   */
  hysteresisMin?: number;
}

/**
 * Where a drop at `cursorAbs` would actually land. Pure — safe to call on every
 * pointermove.
 */
export function computeInsertPreview({
  laneEntries, movingId, durationMin, cursorAbs, windows, baseMs, nowAbs,
  prevIdx = null, hysteresisMin = 0,
}: InsertArgs): InsertPreview {
  const sibs = laneEntries.filter((e) => e.id !== movingId);

  // Only PLANNED work can be reordered — running and completed bars are history.
  // The positional maths runs over those alone, because a lane's completed rows
  // can hold sequence numbers that bear no relation to where they sit on screen
  // (the board packs them by time). Their sequences are left untouched.
  const queue = sibs.filter(isPlanned).sort((a, b) => a.sequence - b.sequence);
  // Nothing may start before the work already on the press has finished.
  const historyEnd = sibs
    .filter((e) => !isPlanned(e))
    .reduce((m, e) => Math.max(m, e.absEnd), -Infinity);
  const floorAbs = Math.max(nowAbs, historyEnd === -Infinity ? -Infinity : historyEnd);

  // Position maps to order: count the bars whose midpoint the cursor has passed.
  const midOf = (s: GanttEntry) => s.absStart + (s.absEnd - s.absStart) / 2;
  let idx = 0;
  for (const s of queue) {
    if (cursorAbs >= midOf(s)) idx += 1;
    else break;
  }

  // Hysteresis: a single-step flip only sticks once the cursor is clearly past
  // the boundary. A multi-step jump means a fast drag — take it as read.
  if (prevIdx != null && hysteresisMin > 0 && Math.abs(idx - prevIdx) === 1) {
    if (idx > prevIdx) {
      const boundary = queue[prevIdx] ? midOf(queue[prevIdx]) : null;
      if (boundary != null && cursorAbs < boundary + hysteresisMin) idx = prevIdx;
    } else {
      const boundary = queue[idx] ? midOf(queue[idx]) : null;
      if (boundary != null && cursorAbs > boundary - hysteresisMin) idx = prevIdx;
    }
  }

  const pred = idx > 0 ? queue[idx - 1] : null;
  const succ = idx < queue.length ? queue[idx] : null;

  // Start = the predecessor's real end. Inserting at the head takes over the
  // planned queue's anchor; an empty queue starts at the drop time. Never
  // earlier than now or than the end of work already run.
  const rawStart = pred
    ? pred.absEnd
    : succ
      ? succ.absStart
      : cursorAbs;
  const startAbs = Math.max(rawStart, floorAbs);

  // Land in front of the successor by taking ITS sequence; append past the
  // whole lane (history included) when there's nothing to land in front of.
  const maxSeq = sibs.reduce((m, e) => Math.max(m, e.sequence), 0);
  const seq = succ ? succ.sequence : maxSeq + 1;

  const split = splitRunAroundDowntime(
    new Date(baseMs + startAbs * 60000).toISOString(),
    durationMin,
    windows,
  );
  const segments = split.segments.map((s) => ({
    startMin: (s.start.getTime() - baseMs) / 60000,
    endMin: (s.end.getTime() - baseMs) / 60000,
  }));
  const endAbs = (split.effectiveEnd.getTime() - baseMs) / 60000;

  return {
    seq,
    startAbs,
    endAbs,
    segments,
    shiftMin: endAbs - startAbs,
    pushedIds: queue.slice(idx).map((e) => e.id),
    beforeJob: succ?.job ?? null,
    afterJob: pred?.job ?? null,
    idx,
  };
}
